/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logo from "@/assets/images/logo.png";
import logoLarge from "@/assets/images/logo-large.png";
import altLogo from "@/assets/images/alt-logo.jpg";
import heroBg from "@/assets/images/hero-bg.jpeg";
import autoPaintingCar from "@/assets/images/auto-painting-car.jpeg";
import carRepairBefore from "@/assets/images/car-repair-before.jpeg";
import carRepairAfter from "@/assets/images/car-repair-after.jpeg";

// Payment method icons
import visaIcon from "@/assets/images/payment/visa.png";
import mastercardIcon from "@/assets/images/payment/mastercard.png";
import discoverIcon from "@/assets/images/payment/discover.png";
import amexIcon from "@/assets/images/payment/amex.png";
import paypalIcon from "@/assets/images/payment/paypal.png";
import cashIcon from "@/assets/images/payment/cash.png";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Logo images
  "/images/logo.png": logo,
  "/assets/images/logo.png": logo,
  "/images/logo-large.png": logoLarge,
  "/assets/images/logo-large.png": logoLarge,
  "/images/alt-logo.jpg": altLogo,
  "/assets/images/alt-logo.jpg": altLogo,
  // Hero background image
  "/images/hero-bg.jpeg": heroBg,
  "/assets/images/hero-bg.jpeg": heroBg,
  // Auto painting car image
  "/images/auto-painting-car.jpeg": autoPaintingCar,
  "/assets/images/auto-painting-car.jpeg": autoPaintingCar,
  // Car repair before/after images
  "/images/car-repair-before.jpeg": carRepairBefore,
  "/assets/images/car-repair-before.jpeg": carRepairBefore,
  "/images/car-repair-after.jpeg": carRepairAfter,
  "/assets/images/car-repair-after.jpeg": carRepairAfter,
  // Payment method icons
  "/images/payment/visa.png": visaIcon,
  "/assets/images/payment/visa.png": visaIcon,
  "/images/payment/mastercard.png": mastercardIcon,
  "/assets/images/payment/mastercard.png": mastercardIcon,
  "/images/payment/discover.png": discoverIcon,
  "/assets/images/payment/discover.png": discoverIcon,
  "/images/payment/amex.png": amexIcon,
  "/assets/images/payment/amex.png": amexIcon,
  "/images/payment/paypal.png": paypalIcon,
  "/assets/images/payment/paypal.png": paypalIcon,
  "/images/payment/cash.png": cashIcon,
  "/assets/images/payment/cash.png": cashIcon,
  // Original crawl paths
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/ddfdf-87h.png": logo,
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/ddfdf-1920w.png": logoLarge,
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/JPG-dea73b24-3694a5d9-e52d8aac-199w.jpg": altLogo,
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/DSC_6421-1920w.jpeg": heroBg,
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/IMG_3087-442fd2d9-7333c360-0d744ebe-fb7d670e-af354b88-0a5fa801-085ac1bc-1920w.jpeg": carRepairBefore,
  "https://lirp.cdn-website.com/08c2665d/dms3rep/multi/opt/IMG_3086-916b02ea-d82868ea-1920w.jpeg": carRepairAfter,
  // Payment method icons from crawl
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/visa.png": visaIcon,
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/mastercard.png": mastercardIcon,
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/discover.png": discoverIcon,
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/amex.png": amexIcon,
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/paypal.png": paypalIcon,
  "https://cdn.website.thryv.com/-resellers-preview/_widget_builder_files/cash.png": cashIcon,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
